export const FROZEN_COLUMNS_PREFERENCE_KEYS = {
  study: "participant_list.frozen_columns.study",
  registry: "participant_list.frozen_columns.registry",
} as const;

export type FrozenColumnsListKey = keyof typeof FROZEN_COLUMNS_PREFERENCE_KEYS;

export const MAX_FROZEN_COLUMNS = 3;

export function getStudyDefaultFrozenKeys(
  availableColumnKeys: string[],
): string[] {
  const defaults = ["participantId", "email", "groupCode"];
  return defaults.filter((key) => availableColumnKeys.includes(key));
}

export function sanitizeFrozenKeys(
  keys: string[],
  availableColumnKeys: string[],
): string[] {
  const available = new Set(availableColumnKeys);
  const seen = new Set<string>();
  const sanitized: string[] = [];

  for (const key of keys) {
    if (!available.has(key) || seen.has(key)) {
      continue;
    }
    seen.add(key);
    sanitized.push(key);
    if (sanitized.length >= MAX_FROZEN_COLUMNS) {
      break;
    }
  }

  return sanitized;
}

export type ColumnDisplayOrderInput = {
  key: string;
  stickyLeftAlways?: boolean;
  stickyRight?: boolean;
};

/**
 * Puts frozen columns in a contiguous left block (after any always-pinned-left
 * columns), with unfrozen columns to their right in natural table order.
 * Unfreezing returns a column to its original position among unfrozen columns.
 */
export function orderColumnsForDisplay<T extends ColumnDisplayOrderInput>(
  columns: T[],
  frozenKeys: string[],
): { displayColumns: T[]; frozenKeysInOrder: string[] } {
  const frozenSet = new Set(frozenKeys);

  const stickyLeftAlways = columns.filter((column) => column.stickyLeftAlways);
  const stickyRight = columns.filter((column) => column.stickyRight);
  const scrollable = columns.filter(
    (column) => !column.stickyLeftAlways && !column.stickyRight,
  );

  const frozen = scrollable.filter((column) => frozenSet.has(column.key));
  const unfrozen = scrollable.filter((column) => !frozenSet.has(column.key));

  return {
    displayColumns: [...stickyLeftAlways, ...frozen, ...unfrozen, ...stickyRight],
    frozenKeysInOrder: frozen.map((column) => column.key),
  };
}

/** Rightmost left-pinned column (always-pinned + frozen); gets the scroll shadow edge. */
export function getLastLeftPinnedColumnKey(
  displayColumns: ColumnDisplayOrderInput[],
  frozenKeys: string[],
): string | null {
  const frozenSet = new Set(frozenKeys);
  let lastKey: string | null = null;

  for (const column of displayColumns) {
    if (column.stickyRight) {
      continue;
    }
    if (column.stickyLeftAlways || frozenSet.has(column.key)) {
      lastKey = column.key;
    } else {
      break;
    }
  }

  return lastKey;
}
